import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Loader2, Store, Trophy, Users, Euro, Gauge, CalendarClock } from 'lucide-react';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);
ChartJS.defaults.color = '#8a8a8a';

const GAME_COLORS = { magic: '#F59E0B', pokemon: '#EF4444', yugioh: '#7B68EE', lorcana: '#4ECDC4', 'one-piece': '#3B82F6', 'flesh-and-blood': '#EC4899' };
const gc = (g, i) => GAME_COLORS[(g || '').toLowerCase()] || ['#FF6B35', '#F59E0B', '#4ECDC4', '#7B68EE', '#EF4444', '#3B82F6', '#EC4899'][i % 7];
const label = (g) => ({ magic: 'Magic', pokemon: 'Pokémon', yugioh: 'Yu-Gi-Oh!', lorcana: 'Lorcana', 'one-piece': 'One Piece', 'flesh-and-blood': 'Flesh & Blood' }[(g || '').toLowerCase()] || (g || 'Autre'));

const KpiCard = ({ icon: Icon, lab, val, sub, acc }) => (
  <div className="relative overflow-hidden bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5" data-testid={`kpi-${lab}`}>
    <div className="absolute top-0 left-0 w-full h-[3px]" style={{ background: acc }} />
    <div className="flex items-center gap-2 text-[#8a8a8a]"><Icon className="w-4 h-4" style={{ color: acc }} /><span className="text-[11px] font-semibold uppercase tracking-wide">{lab}</span></div>
    <div className="text-3xl font-extrabold mt-2 tracking-tight">{val}</div>
    {sub && <div className="text-[11px] text-[#6a6a6a] mt-1">{sub}</div>}
  </div>
);

export default function ShopStatsPage() {
  const { selectedShop, shops } = useAuth();
  const shop = selectedShop || (shops || []).find(s => s.status === 'approved') || (shops || [])[0];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shop) { setLoading(false); return; }
    (async () => {
      try { const res = await api.get(`/shops/${shop.id}/stats`); setData(res.data); }
      catch (e) { setData(null); }
      finally { setLoading(false); }
    })();
  }, [shop]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#FF6B35] animate-spin" /></div>;
  if (!shop || !data) return <div className="min-h-[50vh] flex flex-col items-center justify-center text-center px-4"><Store className="w-12 h-12 text-[#333] mb-3" /><p className="text-[#888]">Aucune boutique sélectionnée ou aucune statistique disponible.</p></div>;

  const k = data.kpis;
  const chartBase = { responsive: true, maintainAspectRatio: false };
  const gridY = { grid: { color: 'rgba(255,255,255,.06)' } };

  const lineData = {
    labels: data.monthly.map(m => m.label),
    datasets: [{
      label: 'Inscriptions', data: data.monthly.map(m => m.value),
      borderColor: '#FF6B35', tension: 0.4, fill: true, borderWidth: 3,
      pointBackgroundColor: '#FF6B35', pointRadius: 5,
      backgroundColor: (c) => { const g = c.chart.ctx.createLinearGradient(0, 0, 0, 260); g.addColorStop(0, 'rgba(255,107,53,.35)'); g.addColorStop(1, 'rgba(255,107,53,0)'); return g; },
    }],
  };
  const pgT = data.per_game_tournaments;
  const doughData = { labels: pgT.map(x => label(x.game)), datasets: [{ data: pgT.map(x => x.count), backgroundColor: pgT.map((x, i) => gc(x.game, i)), borderColor: '#161616', borderWidth: 3 }] };
  const pgR = data.per_game_registrations;
  const barData = { labels: pgR.map(x => label(x.game)), datasets: [{ label: 'Inscrits', data: pgR.map(x => x.count), backgroundColor: pgR.map((x, i) => gc(x.game, i)), borderRadius: 8, barThickness: 46 }] };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="shop-stats-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#E55A25] flex items-center justify-center text-white font-black text-lg">{(data.shop.name || 'M').charAt(0)}</div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">{data.shop.name}</h1>
          <p className="text-[#8a8a8a] text-sm">Tableau de bord · Statistiques</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KpiCard icon={Trophy} lab="Tournois créés" val={k.total_created} sub="depuis l'ouverture" acc="#FF6B35" />
        <KpiCard icon={Users} lab="Total inscrits" val={k.total_registered} sub={`${k.total_created ? (k.total_registered / k.total_created).toFixed(1) : 0} / tournoi`} acc="#4ECDC4" />
        <KpiCard icon={Euro} lab="Revenus" val={`${k.revenue.toLocaleString('fr-FR')} €`} sub="cumulés" acc="#22C55E" />
        <KpiCard icon={Gauge} lab="Remplissage" val={`${k.fill_rate}%`} sub="moyenne des places" acc="#F59E0B" />
        <KpiCard icon={CalendarClock} lab="À venir" val={k.upcoming} sub="tournois planifiés" acc="#7B68EE" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5">
          <h2 className="font-bold">Évolution mensuelle</h2>
          <p className="text-xs text-[#8a8a8a] mb-4">Inscriptions sur les 6 derniers mois</p>
          <div className="relative h-[260px]"><Line data={lineData} options={{ ...chartBase, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ...gridY }, x: { grid: { display: false } } } }} /></div>
        </div>
        <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5">
          <h2 className="font-bold">Tournois par jeu</h2>
          <p className="text-xs text-[#8a8a8a] mb-4">Répartition des {k.total_created} tournois</p>
          <div className="relative h-[260px]"><Doughnut data={doughData} options={{ ...chartBase, cutout: '58%', plugins: { legend: { position: 'right', labels: { boxWidth: 12, padding: 10 } } } }} /></div>
        </div>
      </div>

      <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5">
        <h2 className="font-bold">Inscriptions par jeu</h2>
        <p className="text-xs text-[#8a8a8a] mb-4">Répartition des {k.total_registered} inscrits</p>
        <div className="relative h-[280px]"><Bar data={barData} options={{ ...chartBase, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ...gridY }, x: { grid: { display: false } } } }} /></div>
      </div>
    </div>
  );
}
